import { useCallback, useEffect, useRef, useState } from 'react'
import { phoenixAuthProtocols } from '@/lib/phoenix-auth-token'
import type { RoomCredentials } from '../store/meeting-session.store'
import { currentRoomToken, ensureFreshRoomToken } from '../services/room-token'
import { vclog } from './vc-log'
import { signalingReconnectDelay, REFUSED_AFTER } from '../services/signaling-reconnect'
import {
  createLivenessMonitor,
  ONLINE_PROBE_TIMEOUT_MS,
  type LivenessMonitor,
} from '../services/signaling-liveness'

export type PhoenixEventHandler = (payload: Record<string, unknown>) => void

type ChannelRef = {
  topic: string
  joinRef: string
}

/** What the server said about one command. `status` is Phoenix's own. */
export type PhoenixReply = {
  status: 'ok' | 'error' | 'timeout' | 'dropped'
  response: Record<string, unknown>
}

type UsePhoenixChannelReturn = {
  connected: boolean
  channelJoined: boolean
  sendEvent: (event: string, payload: Record<string, unknown>) => void
  /**
   * Send, and wait for the server to say whether it did it.
   *
   * `sendEvent` is fire-and-forget, which is right for the signalling firehose
   * and wrong for anything a moderator presses: every command the server
   * refuses — a stale role, rooms that are no longer open — is answered with a
   * `phx_reply` that nothing was reading, so the button did nothing and there
   * was no trace of why on either side.
   *
   * Never rejects. A dropped socket or a server that says nothing resolves to
   * `dropped`/`timeout`, because a caller reporting "that did not work" wants
   * the same branch for all three.
   */
  request: (
    event: string,
    payload: Record<string, unknown>,
    timeoutMs?: number,
  ) => Promise<PhoenixReply>
  subscribe: (event: string, handler: PhoenixEventHandler) => () => void
  /**
   * Disconnect WITHOUT `phx_leave`, and stay disconnected.
   *
   * A deliberate leave makes the server end the user's media session at once
   * (`Departures.now`) — for that user id, not for this connection. The Screen
   * Share page shares its id with any other share page of the same account, so
   * when another page has taken over, a leave from this one would end that
   * page's session. A plain disconnect is a dropped connection to the server:
   * it waits out its grace, finds the id still present, and ends nothing.
   */
  abandon: () => void
}

export type PhoenixChannelOptions = {
  /**
   * The `phx_join` payload, asked for on every join attempt — the first one
   * and each automatic rejoin after a dropped socket. Absent means `{}`, which
   * is what the meeting sends.
   *
   * `joinedBefore`: an earlier attempt of this connection was let in. The
   * Screen Share page uses it to take its share id over only on its own first
   * join (contract §7b): a page reconnecting by itself must not take the id
   * back from a page opened since.
   */
  joinPayload?: (attempt: { joinedBefore: boolean }) => Record<string, unknown>
  /**
   * The join reply, on every join the server lets in — the first and each
   * rejoin. Called before `channelJoined` turns true, so what it carries is in
   * place before anything that waits on the channel starts: signaling's TURN
   * credentials (`turn`), which the SFU join builds its transports with.
   */
  onJoined?: (response: Record<string, unknown>) => void
}

export function usePhoenixChannel(
  credentials: RoomCredentials | null,
  options?: PhoenixChannelOptions,
): UsePhoenixChannelReturn {
  const [connected, setConnected] = useState(false)
  const [channelJoined, setChannelJoined] = useState(false)

  const socketRef = useRef<WebSocket | null>(null)
  const channelRef = useRef<ChannelRef | null>(null)
  const msgRefCounter = useRef(1)
  // The current socket's heartbeat, which also decides when it is dead.
  const livenessRef = useRef<LivenessMonitor | null>(null)
  const handlersRef = useRef<Map<string, Set<PhoenixEventHandler>>>(new Map())
  const mountedRef = useRef(true)
  // Commands still waiting to be answered, by the ref they were sent under.
  const pendingRef = useRef<Map<string, (reply: PhoenixReply) => void>>(new Map())
  // Set by the connection effect; see `abandon`.
  const abandonRef = useRef<() => void>(() => {})
  // Read at each join attempt, never a reason to reconnect.
  const optionsRef = useRef(options)
  optionsRef.current = options

  const nextRef = () => String(msgRefCounter.current++)

  const rawSend = useCallback((msg: unknown[]) => {
    const ws = socketRef.current
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg))
      return true
    }
    // Dropped, and said so. A closed or still-opening socket used to swallow
    // the frame without a word, which reads in the UI as a button that does
    // nothing and leaves no trace anywhere to explain it.
    vclog('socket', 'send dropped — socket not open', {
      event: Array.isArray(msg) ? msg[3] : undefined,
      readyState: ws?.readyState ?? 'no socket',
    })
    return false
  }, [])

  const sendEvent = useCallback(
    (event: string, payload: Record<string, unknown>) => {
      const ch = channelRef.current
      // No channel means no topic, and a frame with an empty topic is one the
      // server cannot route: it is discarded silently at the far end, so the
      // command vanishes with nothing logged on either side. Refuse here
      // instead, where it can be seen.
      if (!ch) {
        vclog('socket', 'send dropped — no channel joined', { event })
        return false
      }
      return rawSend([ch.joinRef, nextRef(), ch.topic, event, payload])
    },
    [rawSend],
  )

  const request = useCallback(
    (
      event: string,
      payload: Record<string, unknown>,
      timeoutMs = 8000,
    ): Promise<PhoenixReply> => {
      const ch = channelRef.current
      if (!ch) {
        vclog('socket', 'request dropped — no channel joined', { event })
        return Promise.resolve({ status: 'dropped', response: {} })
      }

      const ref = nextRef()
      return new Promise<PhoenixReply>((resolve) => {
        let settled = false
        const finish = (reply: PhoenixReply) => {
          if (settled) return
          settled = true
          clearTimeout(timer)
          pendingRef.current.delete(ref)
          resolve(reply)
        }

        const timer = setTimeout(() => {
          vclog('socket', 'request timed out', { event })
          finish({ status: 'timeout', response: {} })
        }, timeoutMs)

        pendingRef.current.set(ref, finish)

        if (!rawSend([ch.joinRef, ref, ch.topic, event, payload])) {
          finish({ status: 'dropped', response: {} })
        }
      })
    },
    [rawSend],
  )

  const subscribe = useCallback(
    (event: string, handler: PhoenixEventHandler): (() => void) => {
      if (!handlersRef.current.has(event)) {
        handlersRef.current.set(event, new Set())
      }
      handlersRef.current.get(event)!.add(handler)
      return () => {
        handlersRef.current.get(event)?.delete(handler)
      }
    },
    [],
  )

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!credentials) return

    const { signalingWsUrl, channelTopic, roomToken } = credentials
    // No token, no connection. Signaling refuses a socket without one, and
    // the loop below would retry it for the rest of the meeting — which is
    // exactly what the backend's old "empty token on a failed mint" did to
    // signaling (7,697 refused connections in 20 minutes, 2026-10-06). The
    // backend now answers that case with a 503 instead, so this is the guard
    // for anything that still slips through.
    if (!roomToken) {
      vclog('socket', 'no room token — not connecting to signaling', { channelTopic })
      return
    }
    // signalingWsUrl is like "wss://signal.instavc.com/socket". No token in it:
    // that rides the `Sec-WebSocket-Protocol` header (phoenix-auth-token.ts).
    const wsUrl = `${signalingWsUrl}/websocket?vsn=2.0.0`
    // The room the token is scoped to — the key its renewals are kept under.
    const roomId = channelTopic.replace(/^room:/, '')

    // The one map of pending commands, mutated in place, never replaced.
    const pending = pendingRef.current

    // Reconnection state, scoped to this connection lifetime.
    let intentional = false
    let attempt = 0
    /** Consecutive sockets that closed without ever opening — being refused, not dropped. */
    let refusedStreak = 0
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null
    /** A join of this connection lifetime has been let in (see `joinPayload`). */
    let joinedBefore = false

    const stopLiveness = () => {
      livenessRef.current?.stop()
      livenessRef.current = null
    }

    const reconnectNow = () => {
      reconnectTimer = null
      if (intentional || !mountedRef.current) return
      void ensureFreshRoomToken(roomId, roomToken).then((token) => {
        if (!intentional && mountedRef.current) connect(token)
      })
    }

    const connect = (token: string) => {
      const ws = new WebSocket(wsUrl, phoenixAuthProtocols(token))
      socketRef.current = ws
      let opened = false
      // Joined the channel, not merely opened the socket. A socket the server
      // accepts but whose join it refuses is being turned away just as much as
      // one it never accepted, and backs off the same way.
      let joined = false
      let closed = false
      let joinMsgRef: string | null = null
      // This socket's own heartbeat. The hook-wide ref points at it only while
      // this is the current socket, and nothing this socket does may stop or
      // answer another socket's.
      let monitor: LivenessMonitor | null = null
      const stopMonitor = () => {
        monitor?.stop()
        if (livenessRef.current === monitor) livenessRef.current = null
        monitor = null
      }

      // One way out for this socket, however it ends: the browser's close, a
      // heartbeat that went unanswered, or the server crashing or refusing the
      // channel. Runs once.
      const handleClosed = (why: 'close' | 'unresponsive' | 'join-refused' | 'phx_error' | 'phx_close') => {
        if (closed) return
        closed = true
        stopMonitor()
        if (why !== 'close') {
          // Do not wait for the browser to report it. On a socket whose network
          // path is gone the closing handshake never completes, and `close`
          // would arrive only when the kernel gives up on the connection —
          // ~15 minutes, which is the bug this exists to fix.
          ws.onopen = null
          ws.onmessage = null
          ws.onclose = null
          ws.onerror = null
          try { ws.close() } catch { /* already closing */ }
        }
        if (socketRef.current === ws) socketRef.current = null
        // Nothing on this socket will ever be answered now. Said out loud, or
        // every awaiting caller hangs until its own timeout for no reason.
        for (const settle of pendingRef.current.values()) {
          settle({ status: 'dropped', response: {} })
        }
        pendingRef.current.clear()
        if (!mountedRef.current || intentional) return
        setConnected(false)
        setChannelJoined(false)
        // Reconnect with capped exponential backoff (1s → 10s). Without this a
        // transient drop (network blip, missed heartbeat, backgrounded tab)
        // removes this client from Phoenix Presence permanently — which is why
        // remote tiles sometimes vanish and never come back.
        //
        // A socket refused again and again backs off to a minute instead, and
        // every delay is jittered — see signaling-reconnect.
        attempt += 1
        // Offline, every socket closes before it opens — that is no network,
        // not a server turning us away. Counted as refusals, an outage pushed
        // the backoff toward a minute and kept the `online` handler from
        // reconnecting at once (measured: 20 s lost after a 30 s drop).
        if (!joined && navigator.onLine) {
          refusedStreak += 1
          if (refusedStreak === REFUSED_AFTER) {
            vclog('socket', 'signaling keeps refusing this socket — backing off to a minute', { channelTopic })
          }
        }
        const delay = signalingReconnectDelay(attempt, refusedStreak)
        vclog('socket', 'signaling socket down — will reconnect', {
          channelTopic, why, wasOpen: opened, delayMs: Math.round(delay),
        })
        // With a token that is still good: room tokens last an hour, and
        // reconnecting with the one the meeting started on was refused forever
        // once it had expired.
        reconnectTimer = setTimeout(reconnectNow, delay)
      }

      ws.onopen = () => {
        opened = true
        if (!mountedRef.current) return
        setConnected(true)

        // (Re)join the Phoenix channel. On a reconnect the server replays
        // `presence_state` (after_join), so the roster re-syncs and any peer
        // still connected reappears. Subscriptions live in handlersRef, which
        // survives the reconnect, so no re-subscription is needed.
        const joinRef = nextRef()
        joinMsgRef = nextRef()
        channelRef.current = { topic: channelTopic, joinRef }
        rawSend([joinRef, joinMsgRef, channelTopic, 'phx_join', optionsRef.current?.joinPayload?.({ joinedBefore }) ?? {}])

        // Heartbeat every 30s — and, unlike before, expect it to be answered.
        // An OPEN readyState proves nothing after the network has changed
        // under the socket; see signaling-liveness.
        stopMonitor()
        monitor = createLivenessMonitor({
          nextRef,
          sendHeartbeat: (ref) => {
            if (ws.readyState !== WebSocket.OPEN) return false
            ws.send(JSON.stringify([null, ref, 'phoenix', 'heartbeat', {}]))
            return true
          },
          onDead: ({ reason, waitedMs }) => {
            vclog('socket', 'signaling socket unresponsive — replacing it', { channelTopic, reason, waitedMs })
            handleClosed('unresponsive')
          },
        })
        livenessRef.current = monitor
      }

      ws.onmessage = (evt: MessageEvent) => {
        // Any frame proves the socket, not only a heartbeat reply — see
        // LivenessMonitor.heard.
        monitor?.heard()
        try {
          const [, ref, topic, event, payload] = JSON.parse(evt.data as string) as [
            string | null,
            string | null,
            string,
            string,
            Record<string, unknown>,
          ]

          if (topic === 'phoenix' && event === 'phx_reply') {
            monitor?.ack(ref)
            return
          }

          if (topic === channelTopic && (event === 'phx_error' || event === 'phx_close')) {
            // The channel died on an open socket — the server's channel process
            // crashed, or it closed us. `channelJoined` used to stay true with
            // nothing behind it. A fresh socket rejoins and re-syncs presence.
            vclog('socket', event === 'phx_error'
              ? 'channel crashed on the server — reconnecting'
              : 'channel closed by the server — reconnecting', { channelTopic })
            handleClosed(event)
            return
          }

          if (event === 'phx_reply' && topic === channelTopic) {
            const rep = payload as { status?: string; response?: Record<string, unknown> }
            if (ref !== null && ref === joinMsgRef) {
              if (rep.status === 'ok') {
                joined = true
                joinedBefore = true
                attempt = 0
                refusedStreak = 0
                optionsRef.current?.onJoined?.(rep.response ?? {})
                if (mountedRef.current) setChannelJoined(true)
              } else {
                // Refused (a rejected token, a closed room). Left alone, this
                // open socket sat unjoined for the rest of the meeting and the
                // SFU session — which waits on `channelJoined` — never came back.
                vclog('socket', 'channel join refused — reconnecting', {
                  channelTopic, status: rep.status, response: rep.response,
                })
                // Told to subscribers first. A refusal can be final for the
                // caller: a Screen Share page refused as `share_removed` or
                // `share_superseded` abandons the channel here, and
                // handleClosed then reconnects nothing (contract §7b).
                const refusedHandlers = handlersRef.current.get(event)
                if (refusedHandlers) {
                  for (const h of refusedHandlers) h(payload)
                }
                handleClosed('join-refused')
                return
              }
            }

            // Whoever asked this one, if anyone did.
            const waiting = ref ? pendingRef.current.get(ref) : undefined
            if (waiting) {
              waiting({
                status: rep.status === 'ok' ? 'ok' : 'error',
                response: rep.response ?? {},
              })
            }
          }

          const handlers = handlersRef.current.get(event)
          if (handlers) {
            for (const h of handlers) h(payload)
          }
        } catch {
          // malformed frame — ignore
        }
      }

      ws.onclose = () => handleClosed('close')

      ws.onerror = () => {
        // onclose fires after onerror — reconnect is handled there.
      }
    }

    // The browser saying it is back online is the moment a socket left behind
    // by the outage is most likely dead, and the moment waiting is least
    // useful: probe the live socket now with a tight deadline, or cut short a
    // reconnect backoff that grew while there was no network to reconnect to.
    const onOnline = () => {
      if (livenessRef.current) {
        vclog('socket', 'browser back online — probing signaling socket', { channelTopic })
        livenessRef.current.probe('online', ONLINE_PROBE_TIMEOUT_MS)
      } else if (reconnectTimer !== null && refusedStreak < REFUSED_AFTER) {
        vclog('socket', 'browser back online — reconnecting signaling now', { channelTopic })
        clearTimeout(reconnectTimer)
        // And forget the backoff the outage built up. `online` fires a little
        // before the network carries traffic, so this first try can still
        // fail; its retry should be a second away, not the ~6 s the outage
        // had grown it to.
        attempt = 0
        reconnectNow()
      }
    }
    window.addEventListener('online', onOnline)

    connect(currentRoomToken(roomId, roomToken))

    abandonRef.current = () => {
      intentional = true
      window.removeEventListener('online', onOnline)
      if (reconnectTimer) clearTimeout(reconnectTimer)
      stopLiveness()
      const ws = socketRef.current
      // Cleared first, so nothing below has a channel to send a leave on.
      socketRef.current = null
      channelRef.current = null
      if (ws) {
        ws.onopen = null
        ws.onmessage = null
        ws.onclose = null
        ws.onerror = null
        ws.close()
      }
      for (const settle of pending.values()) {
        settle({ status: 'dropped', response: {} })
      }
      pending.clear()
      setConnected(false)
      setChannelJoined(false)
    }

    return () => {
      abandonRef.current = () => {}
      intentional = true
      window.removeEventListener('online', onOnline)
      if (reconnectTimer) clearTimeout(reconnectTimer)
      stopLiveness()
      const ws = socketRef.current
      if (ws) {
        // Leave channel before closing
        if (ws.readyState === WebSocket.OPEN && channelRef.current) {
          const ch = channelRef.current
          ws.send(JSON.stringify([ch.joinRef, nextRef(), ch.topic, 'phx_leave', {}]))
        }
        // Detached first. Its `close` can come long after this (the closing
        // handshake on a slow or dead path), and a frame can still be in
        // flight on it — and by then the hook's shared state belongs to the
        // socket the next room opened. Left attached, a breakout move's old
        // socket closing late stopped the new socket's heartbeat and dropped
        // the new socket's pending requests.
        ws.onopen = null
        ws.onmessage = null
        ws.onclose = null
        ws.onerror = null
        ws.close()
      }
      // Asked on this socket, so answered on it or not at all.
      for (const settle of pending.values()) {
        settle({ status: 'dropped', response: {} })
      }
      pending.clear()
      socketRef.current = null
      channelRef.current = null
      setConnected(false)
      setChannelJoined(false)
    }
  }, [credentials, rawSend])

  const abandon = useCallback(() => abandonRef.current(), [])

  return { connected, channelJoined, sendEvent, request, subscribe, abandon }
}
